// Cookie-based auth token storage (client side).
import Cookies from "js-cookie";

const TOKEN_KEY = "auth_token";
const TOKEN_VALIDITY_KEY = "token_validity";
const TOKEN_EXPIRES_AT_KEY = "token_expires_at";
const USER_DATA_KEY = "user_data";
const CSRF_TOKEN_KEY = "csrf_token";
const AUTH_CHANGE_EVENT = "ts-auth-change";

function notifyAuthChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(AUTH_CHANGE_EVENT));
}

/**
 * اشتراک در تغییرات کوکی‌های احراز هویت (برای useSyncExternalStore)
 */
export function subscribeToAuthChanges(callback: () => void): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  window.addEventListener(AUTH_CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(AUTH_CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}

function getCookieOptions() {
  return {
    expires: 120,
    path: "/",
    sameSite: "Lax" as const,
    secure: typeof window !== "undefined" && window.location.protocol === "https:",
  };
}

/**
 * دریافت توکن از کوکی
 */
export function getAuthToken(): string | null {
  const token = Cookies.get(TOKEN_KEY);
  return token || null;
}

export function getCsrfToken(): string | null {
  const token = Cookies.get(CSRF_TOKEN_KEY);
  return token || null;
}

export function setAuthToken(token: string, tokenValidity: string, userData: unknown) {
  const cookieOptions = getCookieOptions();
  const validitySeconds = Number.parseInt(tokenValidity, 10);
  const expiresAt =
    Number.isFinite(validitySeconds) && validitySeconds > 0
      ? Date.now() + validitySeconds * 1000
      : Date.now() + 15 * 60 * 1000;

  Cookies.set(TOKEN_KEY, token, cookieOptions);
  Cookies.set(TOKEN_VALIDITY_KEY, tokenValidity, cookieOptions);
  Cookies.set(TOKEN_EXPIRES_AT_KEY, String(expiresAt), cookieOptions);
  Cookies.set(USER_DATA_KEY, JSON.stringify(userData), cookieOptions);
  notifyAuthChange();
}

export function isAuthTokenExpiring(marginMs = 30_000): boolean {
  const token = Cookies.get(TOKEN_KEY);
  if (!token) return false;
  const raw = Cookies.get(TOKEN_EXPIRES_AT_KEY);
  if (!raw) return false;
  const expiresAt = Number.parseInt(raw, 10);
  if (!Number.isFinite(expiresAt)) return false;
  return Date.now() + marginMs >= expiresAt;
}

/**
 * حذف توکن و اطلاعات کاربر از کوکی
 */
export function clearAuthCookies() {
  const cookieOptions = { path: "/" };

  Cookies.remove(TOKEN_KEY, cookieOptions);
  Cookies.remove(TOKEN_VALIDITY_KEY, cookieOptions);
  Cookies.remove(TOKEN_EXPIRES_AT_KEY, cookieOptions);
  Cookies.remove(USER_DATA_KEY, cookieOptions);
  notifyAuthChange();
}

/**
 * دریافت خام اطلاعات کاربر از کوکی (برای snapshot پایدار در useSyncExternalStore)
 */
export function getUserDataRaw(): string | null {
  const userData = Cookies.get(USER_DATA_KEY);
  return userData || null;
}

/**
 * دریافت اطلاعات کاربر از کوکی
 */
export function getUserData<T = unknown>(): T | null {
  const userData = Cookies.get(USER_DATA_KEY);
  if (!userData) return null;

  try {
    return JSON.parse(userData) as T;
  } catch {
    return null;
  }
}

/**
 * به‌روزرسانی اطلاعات کاربر در کوکی (بدون دست زدن به توکن)
 */
export function setUserData(userData: unknown) {
  Cookies.set(USER_DATA_KEY, JSON.stringify(userData), getCookieOptions());
  notifyAuthChange();
}
